<template>
  <div class="env-page">

    <div class="flash is-ok" v-if="flash">
      <span>{{ flash }}</span>
      <button class="flash-close" @click="flash = ''">&times;</button>
    </div>
    <div class="flash is-err" v-if="errorMessage">
      <span>{{ errorMessage }}</span>
      <button class="flash-close" @click="errorMessage = ''">&times;</button>
    </div>

    <!-- Arama -->
    <div class="toolbar">
      <div class="search">
        <i class="fa fa-search"></i>
        <input v-model="query" :placeholder="$t('envelopes.searchPlaceholder')" @input="onSearchInput">
        <button class="clear" v-if="query" @click="query = ''; load(1)">&times;</button>
      </div>

      <div class="view-switch">
        <button :class="{ 'is-active': view === 'cards' }" @click="setView('cards')"
                :title="$t('envelopes.viewCards')">
          <i class="fa fa-th-large"></i>
        </button>
        <button :class="{ 'is-active': view === 'list' }" @click="setView('list')"
                :title="$t('envelopes.viewList')">
          <i class="fa fa-list"></i>
        </button>
      </div>

      <button class="btn-primary" @click="$emit('create')">
        <span class="icon"><i class="fa fa-paper-plane-o"></i></span>
        <span>{{ $t('nav.newEnvelope') }}</span>
      </button>
    </div>

    <!-- Durum filtreleri -->
    <div class="toolbar is-filters">
      <div class="filters">
        <button v-for="option in statusFilters" :key="option.value"
                class="filter" :class="{ 'is-active': status === option.value }"
                @click="setStatus(option.value)">
          {{ option.value ? $t('envelopes.' + option.labelKey) : $t('common.allStatuses') }}
        </button>
      </div>
    </div>

    <div class="skeleton" v-if="loading">{{ $t('envelopes.loadingList') }}</div>

    <!-- Boş -->
    <div class="empty" v-else-if="!list.length">
      <span class="empty-icon"><i class="fa" :class="query ? 'fa-search' : 'fa-inbox'"></i></span>
      <p>{{ query ? $t('envelopes.noMatch') : $t('envelopes.empty') }}</p>
      <button class="btn-ghost" v-if="query" @click="query = ''; load(1)">
        {{ $t('common.clearFilters') }}
      </button>
      <button class="btn-primary" v-else @click="$emit('create')">{{ $t('nav.newEnvelope') }}</button>
    </div>

    <!-- Sıkışık liste: çok kayıtta tek satırda özet -->
    <div class="rows-view" v-else-if="view === 'list'">
      <div class="rows-head">
        <label class="col-pick" :title="$t('envelopes.selectAll')" @click.stop>
          <input type="checkbox" :checked="allOnPageSelected" @change="togglePage">
        </label>
        <span class="col-subject">{{ $t('envelopes.colSubject') }}</span>
        <span class="col-signers">{{ $t('envelopes.colSigners') }}</span>
        <span class="col-status">{{ $t('common.status') }}</span>
        <span class="col-date">{{ $t('envelopes.colUpdated') }}</span>
        <span class="col-act"></span>
      </div>

      <ul class="rows-body">
        <li class="rowx" v-for="item in list" :key="item._id"
            :class="{ 'is-picked': isPicked(item) }" @click="openDetail(item)">

          <label class="col-pick" @click.stop>
            <input type="checkbox" :checked="isPicked(item)" @change="togglePick(item)">
          </label>

          <div class="col-subject">
            <strong>{{ item.subject }}</strong>
            <span class="row-doc" v-if="item.document && item.document.name">
              {{ item.document.name }}
            </span>
          </div>

          <!-- İmzalayanlar: ad üstte, e-posta altta; üçten fazlaysa sayı -->
          <div class="col-signers">
            <span class="mini-who" v-for="r in visibleRecipients(item)" :key="r._id"
                  :class="'is-' + r.status" :title="r.name + ' · ' + r.email">
              <span class="mini-name">{{ r.name }}</span>
              <span class="mini-mail">{{ r.email }}</span>
            </span>
            <span class="more" v-if="hiddenCount(item)"
                  :title="hiddenNames(item)">
              {{ $t('envelopes.moreSigners', { count: hiddenCount(item) }) }}
            </span>
          </div>

          <div class="col-status">
            <span class="pill" :class="statusPill(item.status)">{{ statusLabel(item.status) }}</span>
            <span class="row-count" v-if="signerCount(item)">
              {{ signedCount(item) }}/{{ signerCount(item) }}
            </span>
          </div>

          <div class="col-date">{{ $formatDate(item.updatedAt) }}</div>

          <div class="col-act">
            <button class="icon-btn is-link" v-if="canSend(item)"
                    :title="item.status === 'draft' ? $t('envelopes.send') : $t('envelopes.remind')"
                    @click.stop="send(item)">
              <i class="fa fa-paper-plane-o"></i>
            </button>
            <button class="icon-btn is-ok" v-if="item.status === 'completed'"
                    :title="$t('envelopes.downloadSigned')" @click.stop="download(item, 'signed')">
              <i class="fa fa-download"></i>
            </button>
            <button class="icon-btn is-danger" v-if="canVoid(item)"
                    :title="$t('envelopes.voidAction')" @click.stop="pendingVoid = item">
              <i class="fa fa-ban"></i>
            </button>
            <button class="icon-btn is-danger" :title="$t('envelopes.deleteAction')"
                    @click.stop="askDelete([item])">
              <i class="fa fa-trash-o"></i>
            </button>
          </div>
        </li>
      </ul>
    </div>

    <!-- Kart görünümü -->
    <ul class="cards" v-else>
      <li class="env" v-for="item in list" :key="item._id" :class="{ 'is-picked': isPicked(item) }">

        <label class="env-pick" @click.stop>
          <input type="checkbox" :checked="isPicked(item)" @change="togglePick(item)">
        </label>

        <div class="env-main" @click="openDetail(item)">
          <div class="env-head">
            <h3 class="env-subject">{{ item.subject }}</h3>
            <span class="pill" :class="statusPill(item.status)">{{ statusLabel(item.status) }}</span>
          </div>

          <p class="env-doc" v-if="item.document && item.document.name">
            <i class="fa fa-file-text-o"></i> {{ item.document.name }}
          </p>

          <!-- İmza ilerlemesi: sayı tek başına anlamsız, çubuk durumu
               bir bakışta veriyor -->
          <div class="progress" v-if="signerCount(item)">
            <span class="bar">
              <span class="bar-fill" :style="{ width: progress(item) + '%' }"
                    :class="{ 'is-done': progress(item) === 100 }"></span>
            </span>
            <span class="progress-text">
              {{ $t('envelopes.signCount', { signed: signedCount(item), total: signerCount(item) }) }}
            </span>
          </div>

          <div class="people">
            <span class="who" v-for="r in item.recipients" :key="r._id"
                  :class="'is-' + r.status" :title="r.name + ' · ' + r.email">
              <span class="who-mark">{{ personInitials(r.name || r.email) }}</span>
              <span class="who-name">{{ r.name }}</span>
            </span>
          </div>
        </div>

        <div class="env-side">
          <span class="env-date">{{ $formatDate(item.updatedAt) }}</span>

          <div class="env-actions">
            <button class="icon-btn" :title="$t('envelopes.detail')" @click.stop="openDetail(item)">
              <i class="fa fa-list-alt"></i>
            </button>
            <button class="icon-btn is-link" v-if="canSend(item)"
                    :title="item.status === 'draft' ? $t('envelopes.send') : $t('envelopes.remind')"
                    @click.stop="send(item)">
              <i class="fa fa-paper-plane-o"></i>
            </button>
            <button class="icon-btn" v-if="item.document && item.document.fileName"
                    :title="$t('envelopes.downloadOriginal')" @click.stop="download(item, 'file')">
              <i class="fa fa-file-o"></i>
            </button>
            <button class="icon-btn is-ok" v-if="item.status === 'completed'"
                    :title="$t('envelopes.downloadSigned')" @click.stop="download(item, 'signed')">
              <i class="fa fa-download"></i>
            </button>
            <button class="icon-btn is-danger" v-if="canVoid(item)"
                    :title="$t('envelopes.voidAction')" @click.stop="pendingVoid = item">
              <i class="fa fa-ban"></i>
            </button>
            <button class="icon-btn is-danger" :title="$t('envelopes.deleteAction')"
                    @click.stop="askDelete([item])">
              <i class="fa fa-trash-o"></i>
            </button>
          </div>
        </div>
      </li>
    </ul>

    <!-- Seçim çubuğu: bir şey seçilince altta beliriyor -->
    <div class="pick-bar" v-if="pickedCount">
      <span class="pick-count">{{ $t('envelopes.selectedCount', { count: pickedCount }) }}</span>
      <button class="btn-link" @click="clearPicks">{{ $t('envelopes.clearSelection') }}</button>
      <button class="btn-danger is-solid" @click="askDelete(pickedItems)">
        <i class="fa fa-trash-o"></i>
        <span>{{ $t('envelopes.deleteSelected') }}</span>
      </button>
    </div>

    <!-- Sayfalama -->
    <div class="pager" v-if="total">
      <div class="pager-nav" v-if="pageCount > 1">
        <button class="page-btn" :disabled="page === 1"
                :title="$t('envelopes.first')" @click="load(1)">&laquo;</button>
        <button class="page-btn" :disabled="page === 1" @click="load(page - 1)">&lsaquo;</button>

        <button class="page-btn" v-for="p in pageWindow" :key="p"
                :class="{ 'is-active': p === page }" @click="load(p)">{{ p }}</button>

        <button class="page-btn" :disabled="page === pageCount" @click="load(page + 1)">&rsaquo;</button>
        <button class="page-btn" :disabled="page === pageCount"
                :title="$t('envelopes.last')" @click="load(pageCount)">&raquo;</button>
      </div>

      <div class="pager-side">
        <!-- Kaçıncı kayıtlarda olduğun sayfa numarasından daha anlaşılır -->
        <span class="pager-info">
          {{ $t('envelopes.showing', { from: rangeFrom, to: rangeTo, total: total }) }}
        </span>

        <label class="per-page">
          <span>{{ $t('envelopes.perPage') }}</span>
          <div class="select-wrap">
            <select class="select-input" v-model.number="max" @change="setPageSize">
              <option v-for="size in pageSizes" :value="size" :key="size">{{ size }}</option>
            </select>
          </div>
        </label>
      </div>
    </div>

    <!-- Detay -->
    <div class="sheet" v-if="detail">
      <div class="sheet-backdrop" @click="detail = null"></div>

      <div class="sheet-card is-wide" role="dialog">
        <header class="sheet-head">
          <span class="sheet-icon" :class="statusPill(detail.status)">
            <i class="fa" :class="statusIcon(detail.status)"></i>
          </span>
          <div class="sheet-head-text">
            <h3>{{ detail.subject }}</h3>
            <p>
              <span class="pill" :class="statusPill(detail.status)">{{ statusLabel(detail.status) }}</span>
              <span class="pill" :class="auditValid ? 'is-ok' : 'is-no'">
                {{ auditValid ? $t('envelopes.auditValid') : $t('envelopes.auditBroken') }}
              </span>
            </p>
          </div>
          <button class="sheet-close" @click="detail = null" aria-label="close">&times;</button>
        </header>

        <div class="sheet-body">

          <h4 class="sub-title">{{ $t('envelopes.recipientsTitle') }}</h4>
          <ul class="rows">
            <li class="row" v-for="r in detail.recipients" :key="r._id">
              <span class="row-mark">{{ personInitials(r.name || r.email) }}</span>
              <div class="row-text">
                <strong>{{ r.name }}</strong>
                <span class="row-sub">{{ r.email }}</span>
                <span class="row-sub" v-if="r.signedAt">
                  {{ $formatDate(r.signedAt) }} · {{ r.ip }}
                </span>
                <span class="row-sub" v-else-if="r.declinedAt">
                  {{ $t('envelopes.declineReason') }}: {{ r.declineReason || '—' }}
                </span>
                <span class="row-sub" v-else-if="r.sentAt">
                  {{ $t('envelopes.sentAt') }}: {{ $formatDate(r.sentAt) }}
                </span>
              </div>
              <span class="pill" :class="recipientPill(r.status)">{{ recipientLabel(r.status) }}</span>
            </li>
          </ul>

          <h4 class="sub-title">{{ $t('envelopes.eventsTitle') }}</h4>
          <!-- Zaman çizelgesi: olaylar sıralı ve zincirli, tablo yerine
               akışı gösteren bir biçim daha okunur -->
          <ol class="timeline">
            <li v-for="a in detail.audit" :key="a.seq">
              <span class="tl-dot" :class="'is-' + a.event"></span>
              <div class="tl-text">
                <strong>{{ eventLabel(a.event) }}</strong>
                <span class="tl-meta">
                  {{ $formatDate(a.at) }}
                  <template v-if="a.actorEmail"> · {{ a.actorEmail }}</template>
                  <template v-if="a.detail"> · {{ a.detail }}</template>
                </span>
              </div>
            </li>
          </ol>

          <template v-if="lastLinks && lastLinks.length">
            <h4 class="sub-title">{{ $t('envelopes.linksTitle') }}</h4>
            <div class="link-box" v-for="l in lastLinks" :key="l.email">
              <div class="link-head">
                <strong>{{ l.name }}</strong>
                <span class="pill" :class="l.sent ? 'is-ok' : 'is-warn'">
                  {{ l.sent ? $t('envelopes.mailSent') : $t('envelopes.mailFailed') }}
                </span>
              </div>
              <a class="link-text" :href="l.url" target="_blank">{{ l.url }}</a>
            </div>
          </template>
        </div>

        <footer class="sheet-foot">
          <button class="btn-primary" v-if="canSend(detail)"
                  :class="{ 'is-loading': saving }" @click="send(detail)">
            <span class="icon"><i class="fa fa-paper-plane-o"></i></span>
            <span>{{ detail.status === 'draft' ? $t('envelopes.send') : $t('envelopes.remind') }}</span>
          </button>
          <button class="btn-ok" v-if="detail.status === 'completed'"
                  @click="download(detail, 'signed')">
            <span class="icon"><i class="fa fa-download"></i></span>
            <span>{{ $t('envelopes.downloadSigned') }}</span>
          </button>
          <button class="btn-ghost" @click="detail = null">{{ $t('common.close') }}</button>
        </footer>
      </div>
    </div>

    <!-- İptal -->
    <!-- Silme onayı: neyin silineceği adıyla görünüyor -->
    <div class="sheet" v-if="pendingDelete.length">
      <div class="sheet-backdrop" @click="pendingDelete = []"></div>
      <div class="sheet-card" role="dialog">
        <header class="sheet-head">
          <span class="sheet-icon is-no"><i class="fa fa-trash-o"></i></span>
          <div class="sheet-head-text">
            <h3>{{ $t('envelopes.deleteTitle', { count: pendingDelete.length }) }}</h3>
            <p>{{ $t('envelopes.deleteLead') }}</p>
          </div>
          <button class="sheet-close" @click="pendingDelete = []" aria-label="close">&times;</button>
        </header>

        <div class="sheet-body">
          <ul class="del-list">
            <li v-for="item in pendingDelete.slice(0, 5)" :key="item._id">
              <strong>{{ item.subject }}</strong>
              <span class="pill" :class="statusPill(item.status)">{{ statusLabel(item.status) }}</span>
            </li>
            <li class="del-more" v-if="pendingDelete.length > 5">
              {{ $t('envelopes.deleteMore', { count: pendingDelete.length - 5 }) }}
            </li>
          </ul>

          <p class="note is-warn" v-if="activeInDelete">
            <i class="fa fa-exclamation-triangle"></i>
            {{ $t('envelopes.deleteActiveWarn', { count: activeInDelete }) }}
          </p>
          <p class="note is-info" v-if="completedInDelete">
            <i class="fa fa-shield"></i>
            {{ $t('envelopes.deleteKeepNote') }}
          </p>
        </div>

        <footer class="sheet-foot">
          <button class="btn-danger is-solid" :class="{ 'is-loading': saving }" @click="confirmDelete">
            {{ $t('envelopes.deleteConfirm') }}
          </button>
          <button class="btn-ghost" @click="pendingDelete = []">{{ $t('common.cancel') }}</button>
        </footer>
      </div>
    </div>

    <div class="sheet" v-if="pendingVoid">
      <div class="sheet-backdrop" @click="pendingVoid = null"></div>

      <div class="sheet-card" role="dialog">
        <header class="sheet-head">
          <span class="sheet-icon is-no"><i class="fa fa-ban"></i></span>
          <div class="sheet-head-text">
            <h3>{{ $t('envelopes.voidTitle') }}</h3>
            <p>{{ $t('envelopes.voidBody', { subject: pendingVoid.subject }) }}</p>
          </div>
          <button class="sheet-close" @click="pendingVoid = null" aria-label="close">&times;</button>
        </header>

        <div class="sheet-body">
          <div class="field">
            <label>{{ $t('envelopes.voidReason') }}</label>
            <textarea class="input is-area" rows="3" v-model="voidReason"></textarea>
          </div>
        </div>

        <footer class="sheet-foot">
          <button class="btn-danger is-solid" :class="{ 'is-loading': saving }" @click="doVoid">
            {{ $t('envelopes.voidAction') }}
          </button>
          <button class="btn-ghost" @click="pendingVoid = null">{{ $t('common.cancel') }}</button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../config/api';

// Sayfa boyutu da görünüm gibi hatırlanıyor
function readPageSize() {
  try {
    var saved = parseInt(window.localStorage.getItem('envelopePageSize'), 10);
    return [20, 50, 100].indexOf(saved) > -1 ? saved : 20;
  } catch (e) {
    return 20;
  }
}

// Görünüm tercihi localStorage'da; gizli sekmede erişim hata verebiliyor
function readView() {
  try {
    var saved = window.localStorage.getItem('envelopeView');
    return saved === 'list' ? 'list' : 'cards';
  } catch (e) {
    return 'cards';
  }
}

// Hata durumunda sunucu dosya yerine JSON döner, o da blob olarak gelir
function readBlobAsText(blob) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => resolve('');
    reader.readAsText(blob);
  });
}

export default {
  name: 'Envelopes',
  data() {
    return {
      list: [],
      total: 0,
      page: 1,
      max: readPageSize(),
      pageSizes: [20, 50, 100],
      status: '',

      query: '',
      searchTimer: null,
      // Görünüm tercihi hatırlanıyor: her girişte yeniden seçmek sinir bozucu
      view: readView(),

      loading: false,
      saving: false,
      flash: '',
      errorMessage: '',

      statusFilters: [
        { value: '', labelKey: '' },
        { value: 'sent', labelKey: 'statusSent' },
        { value: 'in_progress', labelKey: 'statusProgress' },
        { value: 'completed', labelKey: 'statusCompleted' },
        { value: 'draft', labelKey: 'statusDraft' },
        { value: 'voided', labelKey: 'statusVoided' }
      ],

      detail: null,
      auditValid: true,
      lastLinks: null,
      pendingVoid: null,

      // Seçim: id -> paket. Sayfa değişince de korunuyor, farklı
      // sayfalardan seçilip birlikte silinebiliyor
      picked: {},
      pendingDelete: [],
      voidReason: ''
    };
  },
  computed: {
    pickedItems() {
      return Object.keys(this.picked).map((id) => this.picked[id]);
    },
    pickedCount() {
      return this.pickedItems.length;
    },
    allOnPageSelected() {
      return this.list.length > 0 && this.list.every((item) => !!this.picked[item._id]);
    },
    activeInDelete() {
      return this.pendingDelete.filter((e) => e.status === 'sent' || e.status === 'in_progress').length;
    },
    completedInDelete() {
      return this.pendingDelete.filter((e) => e.status === 'completed').length;
    },
    rangeFrom() {
      return this.total ? (this.page - 1) * this.max + 1 : 0;
    },
    rangeTo() {
      return Math.min(this.page * this.max, this.total);
    },
    pageCount() {
      return Math.max(Math.ceil(this.total / this.max), 1);
    },
    pageWindow() {
      const span = 2;
      const start = Math.max(1, Math.min(this.page - span, this.pageCount - span * 2));
      const end = Math.min(this.pageCount, start + span * 2);
      const pages = [];
      for (let p = start; p <= end; p++) pages.push(p);
      return pages;
    }
  },
  mounted() {
    this.load(1);
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    isPicked(item) {
      return !!this.picked[item._id];
    },
    togglePick(item) {
      if (this.picked[item._id]) this.$delete(this.picked, item._id);
      else this.$set(this.picked, item._id, item);
    },
    togglePage() {
      if (this.allOnPageSelected) {
        this.list.forEach((item) => this.$delete(this.picked, item._id));
      } else {
        this.list.forEach((item) => this.$set(this.picked, item._id, item));
      }
    },
    clearPicks() {
      this.picked = {};
    },

    askDelete(items) {
      this.pendingDelete = items.slice();
    },

    confirmDelete() {
      if (this.saving || !this.pendingDelete.length) return;
      this.saving = true;
      const ids = this.pendingDelete.map((e) => e._id);

      api.post('/envelopes/delete', { ids: ids })
        .then((body) => {
          // Silinenleri seçimden de düş
          (body.deleted || ids).forEach((id) => this.$delete(this.picked, id));
          this.pendingDelete = [];
          this.flash = body.message;
          this.load(this.page);
        })
        .catch((err) => {
          this.pendingDelete = [];
          this.errorMessage = err.message;
        })
        .then(() => { this.saving = false; });
    },

    load(page) {
      // pageCount yükleme öncesi eski toplamdan hesaplandığı için burada
      // yalnızca alt sınırı uyguluyoruz; üst sınırı cevap gelince düzeltiyoruz
      this.page = Math.max(page || 1, 1);
      this.loading = true;
      api.get('/envelopes', {
        params: {
          page: this.page,
          max: this.max,
          status: this.status || undefined,
          q: this.query || undefined
        }
      })
        .then((body) => {
          this.list = body.envelopeList;
          this.total = body.total;

          // Son sayfadaki kayıtlar silinince veya arama daraltınca
          // boş bir sayfada kalınabiliyor; son dolu sayfaya çekiyoruz
          var lastPage = Math.max(Math.ceil(this.total / this.max), 1);
          if (this.page > lastPage) {
            this.load(lastPage);
            return;
          }
          // Uzun listede sayfa değişince başa dön
          if (this.$el && this.$el.scrollIntoView) {
            this.$el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        })
        .catch((err) => { this.errorMessage = err.message; })
        .then(() => { this.loading = false; });
    },

    openDetail(item) {
      this.lastLinks = null;
      api.get('/envelopes/' + item._id)
        .then((body) => {
          this.detail = body.envelope;
          this.auditValid = body.auditValid;
        })
        .catch((err) => { this.errorMessage = err.message; });
    },

    // Taslakta ilk gönderim, süren pakette hatırlatma.
    // Hatırlatma yeni token üretir, eski link geçersiz olur.
    canSend(item) {
      return ['draft', 'sent', 'in_progress'].indexOf(item.status) > -1;
    },
    canVoid(item) {
      return ['completed', 'voided', 'declined'].indexOf(item.status) === -1;
    },

    send(item) {
      if (this.saving) return;
      this.saving = true;
      api.post('/envelopes/' + item._id + '/send')
        .then((body) => {
          this.flash = body.message;

          // Pencereden gönderildiyse: her e-posta gittiyse pencere kapansın.
          // Gidemeyen varsa açık kalsın; o alıcının bağlantısını kopyalayıp
          // elle iletmenin tek yolu penceredeki bağlantı kutusu.
          if (this.detail && this.detail._id === item._id) {
            const failed = (body.links || []).filter((l) => !l.sent);
            if (failed.length) {
              this.detail = body.envelope;
              this.lastLinks = body.links;
            } else {
              this.detail = null;
              this.lastLinks = null;
            }
          } else {
            this.lastLinks = body.links;
          }
          this.load(this.page);
        })
        .catch((err) => { this.errorMessage = err.message; })
        .then(() => { this.saving = false; });
    },

    doVoid() {
      if (this.saving) return;
      this.saving = true;
      api.post('/envelopes/' + this.pendingVoid._id + '/void', { reason: this.voidReason })
        .then((body) => {
          this.flash = body.message;
          this.pendingVoid = null;
          this.voidReason = '';
          this.load(this.page);
        })
        .catch((err) => { this.errorMessage = err.message; })
        .then(() => { this.saving = false; });
    },

    download(item, kind) {
      const url = '/envelopes/' + item._id + (kind === 'signed' ? '/signed' : '/file');
      api.get(url, { responseType: 'blob' })
        .then((blob) => {
          // Sunucu 304 dönerse gövde boş gelir ve tarayıcı 0 baytlık bir
          // dosya indirir; kullanıcı da neden açılmadığını arar.
          if (!blob || blob.size === 0) {
            this.errorMessage = this.$t('common.emptyFileError');
            return;
          }
          if (blob.type && blob.type.indexOf('application/json') > -1) {
            return readBlobAsText(blob).then((text) => {
              let message = this.$t('common.downloadFailed');
              try { message = JSON.parse(text).message || message; } catch (e) { /* JSON değil */ }
              this.errorMessage = message;
            });
          }
          const href = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = href;
          a.download = kind === 'signed'
            ? item.subject + ' ' + this.$t('envelopes.signedSuffix') + '.pdf'
            : (item.document && item.document.fileName) || 'belge.pdf';
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(href);
        })
        .catch((err) => { this.errorMessage = err.message; });
    },

    setPageSize() {
      try { window.localStorage.setItem('envelopePageSize', String(this.max)); } catch (e) { /* yazamıyorsak da bu oturum için geçerli */ }
      this.load(1);
    },

    setView(value) {
      this.view = value;
      try { window.localStorage.setItem('envelopeView', value); } catch (e) { /* yazamıyorsak da bu oturum için değişti */ }
    },

    // Her tuşta istek atmıyoruz; yazmayı bitirmesini bekliyoruz
    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => { this.load(1); }, 350);
    },

    // Listede en fazla üç kişi gösteriliyor, kalanı sayı olarak
    visibleRecipients(item) {
      return (item.recipients || []).slice(0, 3);
    },
    hiddenCount(item) {
      return Math.max((item.recipients || []).length - 3, 0);
    },
    hiddenNames(item) {
      return (item.recipients || []).slice(3)
        .map(function (r) { return r.name + ' <' + r.email + '>'; })
        .join('\n');
    },

    setStatus(value) {
      this.status = value;
      this.load(1);
    },

    progress(item) {
      const total = this.signerCount(item);
      return total ? Math.round((this.signedCount(item) / total) * 100) : 0;
    },

    personInitials(source) {
      let text = String(source || '');
      const at = text.indexOf('@');
      if (at > 0) text = text.slice(0, at);
      const parts = text.trim().split(/[\s._-]+/).filter(Boolean);
      if (!parts.length) return '?';
      const locale = this.$locale === 'tr' ? 'tr-TR' : 'en-US';
      const take = parts.length === 1
        ? parts[0].slice(0, 2)
        : parts[0][0] + parts[parts.length - 1][0];
      return take.toLocaleUpperCase(locale);
    },

    signerCount(item) {
      return (item.recipients || []).filter((r) => r.action !== 'cc').length;
    },
    signedCount(item) {
      return (item.recipients || []).filter((r) => r.status === 'signed').length;
    },

    statusLabel(status) {
      const map = { draft: 'statusDraft', sent: 'statusSent', in_progress: 'statusProgress',
        completed: 'statusCompleted', declined: 'statusDeclined', voided: 'statusVoided' };
      return map[status] ? this.$t('envelopes.' + map[status]) : status;
    },
    statusPill(status) {
      return {
        draft: 'is-idle', sent: 'is-info', in_progress: 'is-warn',
        completed: 'is-ok', declined: 'is-no', voided: 'is-dark'
      }[status] || 'is-idle';
    },

    // Durum ikonu: renk tek başına yeterli değil, biçim de ayırt etsin
    statusIcon(status) {
      return {
        draft: 'fa-pencil', sent: 'fa-paper-plane-o', in_progress: 'fa-hourglass-half',
        completed: 'fa-check', declined: 'fa-times', voided: 'fa-ban'
      }[status] || 'fa-file-o';
    },
    recipientLabel(status) {
      const map = { pending: 'rcpPending', sent: 'rcpSent', viewed: 'rcpViewed',
        signed: 'rcpSigned', declined: 'rcpDeclined' };
      return map[status] ? this.$t('envelopes.' + map[status]) : status;
    },
    recipientPill(status) {
      return {
        pending: 'is-idle', sent: 'is-info', viewed: 'is-warn',
        signed: 'is-ok', declined: 'is-no'
      }[status] || 'is-idle';
    },
    eventLabel(event) {
      const map = { created: 'evCreated', sent: 'evSent', mailed: 'evMailed',
        viewed: 'evViewed', signed: 'evSigned', completed: 'evCompleted',
        declined: 'evDeclined', voided: 'evVoided', rebuilt: 'evRebuilt' };
      return map[event] ? this.$t('envelopes.' + map[event]) : event;
    },
    formatDate(value) {
      return this.$formatDate(value);
    }
  }
};
</script>
<style scoped>
.env-page { max-width: 880px; margin: 0 auto; }

/* --- Bildirim --- */
.flash {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 14px;
  margin-bottom: 16px;
}

.flash.is-ok { background: #e9f7f2; color: #0f6e5c; }
.flash.is-err { background: #fdeceb; color: #a8322c; }

.flash-close {
  background: transparent;
  border: 0;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  color: inherit;
  opacity: 0.6;
}

/* --- Araç çubuğu --- */
.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 18px;
}

/* Açılır liste yerine görünür filtreler: hangi filtrenin açık olduğu
   ve başka hangi seçeneklerin bulunduğu aynı anda görünüyor */
.filters { display: flex; flex-wrap: wrap; gap: 6px; }

.filter {
  background: #fff;
  border: 1px solid #e2e6f0;
  border-radius: 999px;
  padding: 7px 14px;
  font-size: 13px;
  font-weight: 600;
  color: #79809a;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s, background-color 0.15s;
}

.filter:hover { border-color: #c3cbdd; color: #4a5175; }

.filter.is-active {
  background: #22223d;
  border-color: #22223d;
  color: #fff;
}

/* --- Boş / yükleniyor --- */
.skeleton,
.empty {
  background: #fff;
  border: 1px solid #e6e8f0;
  border-radius: 12px;
  padding: 44px 20px;
  text-align: center;
  color: #98a0b5;
}

.empty-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 17px;
  background: #f2f4fa;
  color: #b3bacb;
  font-size: 22px;
  margin-bottom: 14px;
}

.empty p { font-size: 14.5px; color: #79809a; margin-bottom: 16px; }

/* --- Paket kartı --- */
.cards { display: flex; flex-direction: column; gap: 10px; }

.env {
  display: flex;
  align-items: stretch;
  gap: 16px;
  background: #fff;
  border: 1px solid #e6e8f0;
  border-radius: 12px;
  padding: 16px 18px;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.env:hover {
  border-color: #cfd6e6;
  box-shadow: 0 4px 14px rgba(24, 24, 48, 0.07);
}

.env-main { flex: 1 1 auto; min-width: 0; cursor: pointer; }

.env-head {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.env-subject {
  font-size: 15px;
  font-weight: 700;
  color: #1e2233;
  line-height: 1.3;
}

.env-doc {
  font-size: 12.5px;
  color: #8b93a8;
  margin-top: 4px;
}

.env-doc i { margin-right: 5px; opacity: 0.7; }

/* --- İlerleme --- */
.progress {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 11px;
}

.bar {
  flex: 0 0 120px;
  height: 5px;
  border-radius: 3px;
  background: #eceef6;
  overflow: hidden;
}

.bar-fill {
  display: block;
  height: 100%;
  border-radius: 3px;
  background: #7d86b4;
  transition: width 0.3s ease;
}

.bar-fill.is-done { background: linear-gradient(90deg, #00d1b2, #00a88f); }

.progress-text { font-size: 12px; color: #8b93a8; font-variant-numeric: tabular-nums; }

/* --- Alıcılar --- */
.people {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 11px;
}

.who {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f4f6fb;
  border-radius: 999px;
  padding: 3px 11px 3px 3px;
  font-size: 12px;
  color: #5a6180;
  max-width: 190px;
}

.who-mark {
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #dfe4f1;
  color: #4a5175;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
}

.who-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* İmzalayan yeşil, reddeden kırmızı: satırı okumadan kimin ne yaptığı belli */
.who.is-signed { background: #e4f6ef; color: #0f6e5c; }
.who.is-signed .who-mark { background: #bfe8db; color: #0c5c4c; }
.who.is-declined { background: #fdeceb; color: #a8322c; }
.who.is-declined .who-mark { background: #f6cdca; color: #8e2b26; }
.who.is-viewed .who-mark { background: #f7e3c4; color: #8a6116; }

/* --- Sağ sütun --- */
.env-side {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
}

.env-date { font-size: 12px; color: #98a0b5; white-space: nowrap; }

.env-actions { display: flex; gap: 4px; }

.icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e2e6f0;
  background: #fff;
  color: #79809a;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s, background-color 0.15s;
}

.icon-btn:hover { border-color: #c3cbdd; color: #3c4257; }
.icon-btn.is-link:hover { border-color: #7d86b4; color: #3b4480; }
.icon-btn.is-ok:hover { border-color: #00b89c; color: #0b6e5f; background: #f2fcfa; }
.icon-btn.is-danger:hover { border-color: #e0a9a5; color: #b63934; background: #fdf3f2; }

/* --- Sayfalama --- */
.pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 18px;
}

.pager-nav { display: flex; align-items: center; gap: 5px; }

.pager-side {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-left: auto;
}

.per-page {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12.5px;
  color: #98a0b5;
}

.per-page .select-wrap { width: 72px; }

.per-page .select-input {
  width: 100%;
  height: 32px;
  border: 1px solid #e2e6f0;
  border-radius: 8px;
  padding: 0 26px 0 10px;
  font-size: 12.5px;
  color: #3c4257;
  background: #fff;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  cursor: pointer;
}

.per-page .select-wrap { position: relative; }

.per-page .select-wrap::after {
  content: "";
  position: absolute;
  right: 10px;
  top: 50%;
  margin-top: -2px;
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid #8b93a8;
  pointer-events: none;
}

.page-btn {
  min-width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e2e6f0;
  background: #fff;
  color: #5a6180;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.page-btn:hover:not(:disabled) { border-color: #c3cbdd; }
.page-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.page-btn.is-active { background: #22223d; border-color: #22223d; color: #fff; }

.pager-info {
  font-size: 12.5px;
  color: #98a0b5;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* --- Pencere --- */
.sheet {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.sheet-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(20, 20, 38, 0.55);
  backdrop-filter: blur(3px);
}

.sheet-card {
  position: relative;
  width: 480px;
  max-width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(16, 16, 36, 0.35);
  overflow: hidden;
  animation: sheet-in 0.18s ease-out;
}

.sheet-card.is-wide { width: 620px; }

@keyframes sheet-in {
  from { opacity: 0; transform: translateY(10px) scale(0.985); }
  to { opacity: 1; transform: none; }
}

.sheet-head {
  display: flex;
  align-items: flex-start;
  gap: 13px;
  padding: 20px 20px 16px;
  border-bottom: 1px solid #eef0f6;
}

.sheet-icon {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.sheet-head-text { flex: 1 1 auto; min-width: 0; }

.sheet-head-text h3 {
  font-size: 16px;
  font-weight: 700;
  color: #1e2233;
  line-height: 1.3;
  word-break: break-word;
}

.sheet-head-text p {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  font-size: 12.5px;
  color: #8b93a8;
  margin-top: 6px;
  line-height: 1.5;
}

.sheet-close {
  flex: 0 0 auto;
  background: transparent;
  border: 0;
  font-size: 24px;
  line-height: 1;
  color: #b3bacb;
  cursor: pointer;
}

.sheet-close:hover { color: #6b7283; }

.sheet-body { padding: 16px 20px 6px; overflow-y: auto; }

.sheet-foot {
  display: flex;
  gap: 8px;
  padding: 16px 20px;
  border-top: 1px solid #eef0f6;
  background: #fbfcfe;
}

.sub-title {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #9aa1b5;
  margin: 6px 0 9px;
}

.sub-title:not(:first-child) { margin-top: 20px; }

/* --- Satır listesi --- */
.rows { border: 1px solid #eef0f6; border-radius: 10px; overflow: hidden; }

.row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 13px;
  border-bottom: 1px solid #f1f3f8;
}

.row:last-child { border-bottom: 0; }

.row-mark {
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: linear-gradient(140deg, #eef1fb 0%, #e3e7f7 100%);
  color: #4a5175;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
}

.row-text { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; }
.row-text strong { font-size: 13.5px; color: #22283a; font-weight: 600; }
.row-sub { font-size: 12px; color: #8b93a8; word-break: break-word; }

/* --- Zaman çizelgesi --- */
.timeline { position: relative; padding-left: 6px; }

.timeline li {
  position: relative;
  display: flex;
  gap: 12px;
  padding: 0 0 15px 16px;
  border-left: 2px solid #eef0f6;
}

.timeline li:last-child { border-left-color: transparent; padding-bottom: 2px; }

.tl-dot {
  position: absolute;
  left: -6px;
  top: 3px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #c8cfe0;
  box-shadow: 0 0 0 3px #fff;
}

.tl-dot.is-signed,
.tl-dot.is-completed { background: #00c2a4; }
.tl-dot.is-declined,
.tl-dot.is-voided { background: #d8615b; }
.tl-dot.is-mailed,
.tl-dot.is-sent { background: #6f8bd0; }

.tl-text { display: flex; flex-direction: column; gap: 2px; }
.tl-text strong { font-size: 13px; color: #22283a; font-weight: 600; }
.tl-meta { font-size: 11.5px; color: #98a0b5; line-height: 1.5; }

/* --- Link kutusu --- */
.link-box {
  background: #f4f7fb;
  border: 1px solid #e6eaf3;
  border-radius: 10px;
  padding: 11px 13px;
  margin-bottom: 8px;
}

.link-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 5px;
}

.link-head strong { font-size: 13px; color: #22283a; }

.link-text {
  display: block;
  font-size: 12px;
  color: #0b6e5f;
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  word-break: break-all;
}

/* --- Form --- */
.field label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #5a6180;
  margin-bottom: 5px;
}

.input.is-area {
  width: 100%;
  border: 1px solid #dde1ea;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  color: #2b3146;
  line-height: 1.6;
  resize: vertical;
}

.input.is-area:focus {
  outline: 0;
  border-color: #00b89c;
  box-shadow: 0 0 0 3px rgba(0, 209, 178, 0.16);
}

/* --- Rozetler --- */
.pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border-radius: 999px;
  padding: 3px 11px;
  font-size: 11.5px;
  font-weight: 700;
  white-space: nowrap;
}

.pill.is-ok { background: #e4f6ef; color: #0f6e5c; }
.pill.is-no { background: #fdeceb; color: #a8322c; }
.pill.is-idle { background: #f2f4fa; color: #7a819a; }
.pill.is-info { background: #e8effc; color: #2d5eaa; }
.pill.is-warn { background: #fdf2e2; color: #a66a12; }
.pill.is-dark { background: #e9eaf0; color: #5a6180; }

/* --- Düğmeler --- */
.btn-primary,
.btn-ghost,
.btn-danger,
.btn-ok {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border-radius: 9px;
  padding: 10px 17px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s;
}

.btn-primary {
  background: linear-gradient(135deg, #00d1b2 0%, #00a88f 100%);
  border: 1px solid transparent;
  color: #06302a;
  box-shadow: 0 3px 10px rgba(0, 168, 143, 0.28);
}

.btn-primary:hover { background: linear-gradient(135deg, #0ddcbe 0%, #00b79c 100%); }
.btn-primary.is-loading { opacity: 0.7; pointer-events: none; }

.btn-ok { background: #fff; border: 1px solid #bfe8db; color: #0b6e5f; }
.btn-ok:hover { background: #f2fcfa; }

.btn-ghost { background: #fff; border: 1px solid #dde1ea; color: #3c4257; }
.btn-ghost:hover { border-color: #b9c0d2; }

.btn-danger { background: #fff; border: 1px solid #f0cdcb; color: #b63934; }
.btn-danger:hover { background: #fdf3f2; }

.btn-danger.is-solid {
  background: #c4413c;
  border-color: #c4413c;
  color: #fff;
}

.btn-danger.is-solid:hover { background: #ad3833; }


/* --- Arama --- */
.search {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
}

.search > i {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: #a7aec1;
  font-size: 13px;
}

.search input {
  width: 100%;
  height: 40px;
  border: 1px solid #e2e6f0;
  border-radius: 9px;
  padding: 0 34px;
  font-size: 14px;
  color: #2b3146;
  background: #fff;
}

.search input:focus {
  outline: 0;
  border-color: #00b89c;
  box-shadow: 0 0 0 3px rgba(0, 209, 178, 0.16);
}

.clear {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: 0;
  font-size: 18px;
  color: #a7aec1;
  cursor: pointer;
}

/* --- Görünüm seçici --- */
.view-switch {
  display: flex;
  background: #eceef6;
  border-radius: 9px;
  padding: 3px;
  gap: 2px;
  flex: 0 0 auto;
}

.view-switch button {
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #8b93a8;
  cursor: pointer;
  font-size: 13px;
}

.view-switch button.is-active {
  background: #fff;
  color: #22223d;
  box-shadow: 0 1px 3px rgba(24, 24, 48, 0.14);
}

.toolbar.is-filters { margin-top: -6px; }

/* --- Sıkışık liste --- */
.rows-view {
  background: #fff;
  border: 1px solid #e6e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.rows-head,
.rowx {
  display: grid;
  grid-template-columns: 28px minmax(0, 2.2fr) minmax(0, 2.4fr) 150px 120px 140px;
  gap: 14px;
  align-items: center;
  padding: 11px 16px;
}

.rows-head {
  background: #fafbfe;
  border-bottom: 1px solid #eef0f6;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #9aa1b5;
}

.rowx {
  border-bottom: 1px solid #f3f5fa;
  cursor: pointer;
  transition: background-color 0.12s;
}

.rows-body .rowx:last-child { border-bottom: 0; }
.rowx:hover { background: #fafbfe; }

.col-subject strong {
  display: block;
  font-size: 13.5px;
  font-weight: 600;
  color: #22283a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.row-doc {
  display: block;
  font-size: 11.5px;
  color: #98a0b5;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 2px;
}

/* İmzalayan: ad üstte, e-posta altta — ikisi de gerekli ama
   e-posta ikincil, küçük ve soluk */
.col-signers {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 14px;
  min-width: 0;
}

.mini-who { min-width: 0; max-width: 180px; line-height: 1.3; }

.mini-name {
  display: block;
  font-size: 12.5px;
  color: #3c4257;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mini-mail {
  display: block;
  font-size: 11px;
  color: #a2a9bd;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Durum rengi adın soluna ince bir çizgi olarak: rozet kalabalık yapıyordu */
.mini-who { border-left: 2px solid #dfe3ee; padding-left: 7px; }
.mini-who.is-signed { border-left-color: #00c2a4; }
.mini-who.is-declined { border-left-color: #d8615b; }
.mini-who.is-viewed { border-left-color: #e9b155; }
.mini-who.is-sent { border-left-color: #8ba4dd; }

.mini-who.is-signed .mini-name { color: #0f6e5c; }
.mini-who.is-declined .mini-name { color: #a8322c; }

.more {
  align-self: center;
  font-size: 11.5px;
  font-weight: 700;
  color: #8b93a8;
  background: #f2f4fa;
  border-radius: 999px;
  padding: 2px 9px;
  cursor: default;
}

.col-status { display: flex; align-items: center; gap: 8px; }

.row-count {
  font-size: 11.5px;
  color: #98a0b5;
  font-variant-numeric: tabular-nums;
}

.col-date {
  font-size: 12px;
  color: #98a0b5;
  white-space: nowrap;
}

.col-act { display: flex; gap: 4px; justify-content: flex-end; }

@media screen and (max-width: 900px) {
  .rows-head { display: none; }

  .rowx {
    grid-template-columns: 28px 1fr;
    gap: 8px;
    padding: 14px 16px;
  }

  .col-act { justify-content: flex-start; }
}

/* --- Seçim ve silme --- */
.col-pick,
.env-pick {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.col-pick input,
.env-pick input {
  width: 17px;
  height: 17px;
  accent-color: #00a88f;
  cursor: pointer;
}

.env-pick { flex: 0 0 auto; padding-top: 2px; align-self: flex-start; }

.rowx.is-picked { background: #f2fbf9; }
.env.is-picked { border-color: #9fdccf; background: #f7fdfb; }

/* Seçim çubuğu ekranın altında sabit: uzun listede de erişilebilir */
.pick-bar {
  position: sticky;
  bottom: 16px;
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 16px;
  background: #1c1c33;
  color: #eef0f7;
  border-radius: 12px;
  padding: 12px 16px 12px 20px;
  box-shadow: 0 10px 30px rgba(20, 20, 40, 0.28);
}

.pick-count { font-size: 14px; font-weight: 700; }
.pick-bar .btn-link { color: #aeb4cc; }
.pick-bar .btn-link:hover { color: #fff; }
.pick-bar .btn-danger { margin-left: auto; }

.btn-link {
  background: transparent;
  border: 0;
  font-size: 13px;
  font-weight: 600;
  color: #79809a;
  cursor: pointer;
  padding: 4px 2px;
}

/* Onay penceresindeki liste */
.del-list {
  list-style: none;
  border: 1px solid #eef0f6;
  border-radius: 10px;
  margin-bottom: 14px;
}

.del-list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 13px;
  border-bottom: 1px solid #f1f3f8;
}

.del-list li:last-child { border-bottom: 0; }

.del-list strong {
  font-size: 13.5px;
  color: #22283a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.del-more { font-size: 12.5px; color: #8b93a8; }

.note {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  border-radius: 10px;
  padding: 11px 13px;
  font-size: 12.5px;
  line-height: 1.55;
  margin-bottom: 10px;
}

.note.is-warn { background: #fdf5e6; color: #7d5713; }
.note.is-info { background: #eaf1fd; color: #24508f; }
.note i { margin-top: 2px; }

@media screen and (max-width: 720px) {
  .toolbar { flex-direction: column; align-items: stretch; }
  .view-switch { align-self: flex-start; }
  .toolbar .btn-primary { justify-content: center; }
  .env { flex-direction: column; gap: 12px; }
  .env-side { flex-direction: row; align-items: center; justify-content: space-between; }
  .sheet { padding: 0; align-items: flex-end; }
  .sheet-card { width: 100%; border-radius: 16px 16px 0 0; max-height: 92vh; }
  .pager { flex-direction: column; align-items: stretch; }
  .pager-side { margin-left: 0; justify-content: space-between; }
}
</style>


// WEBPACK FOOTER //
// src/Components/Envelopes.vue